import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputBase from '@mui/material/InputBase';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { hasPermission } from '@relif/zeller-roles';
import {
  BackButton,
  FormField,
  FormGrid,
  FormSection,
  InlineFeedback,
  PrimaryButton,
  RutField,
} from 'components/forms';
import useCurrentUser from 'hooks/useCurrentUser';
import type React from 'react';
import { type FC, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from 'redux/store';
import { businessUnitsApi, useGetUserBusinessUnitsQuery } from 'services/api/businessUnits.api';
import { useCreateUserMutation, useGetUsersQuery, useUpdateUserMutation } from 'services/api/user.api';
import type { TemplateResponse } from 'services/types/message';
import type { AssignmentTeam, User, UserAttributes } from 'services/types/user';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_ITEM,
  SHADOW,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';
import validateEmail from 'utils/validations/validateEmail';
import validateRut from 'utils/validations/validateRut';

import WorkingHoursEditor from './WorkingHoursEditor';

// ── DS-aligned dialog surface ──────────────────────────────────────────────

const glassPaperSx = {
  backdropFilter: GLASS_BLUR,
  backgroundColor: GLASS_BACKGROUND,
  boxShadow: SHADOW,
  borderRadius: RADIUS_CARD,
  border: `1px solid ${alpha(NAVY, 0.06)}`,
} as const;

const glassBackdropSx = {
  backdropFilter: 'blur(10px)',
  backgroundColor: alpha(NAVY, 0.25),
} as const;

const chipSx = {
  borderRadius: RADIUS_ITEM,
  backgroundColor: alpha(NAVY, 0.06),
  color: NAVY,
  fontFamily: 'Jost, sans-serif',
  fontWeight: 500,
} as const;

// ── Local helper components ────────────────────────────────────────────────

interface FieldShellProps {
  label: string;
  required?: boolean;
  optional?: boolean;
  error?: string | null;
  hint?: ReactNode;
  children: ReactNode;
}

/** Mirrors FormField's label/helper visuals around custom MUI controls (multi-selects). */
const FieldShell: FC<FieldShellProps> = ({ label, required, optional, error, hint, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
    <Typography
      component="label"
      sx={{
        fontFamily: 'Jost, sans-serif',
        fontSize: '0.8125rem',
        fontWeight: 500,
        color: alpha(NAVY, 0.7),
        mb: 0.875,
        letterSpacing: '0.005em',
        display: 'inline-flex',
        alignItems: 'center',
      }}
    >
      {label}
      {required && (
        <Typography component="span" sx={{ color: '#E7233B', fontWeight: 600, ml: 0.25 }} aria-hidden>
          *
        </Typography>
      )}
      {optional && !required && (
        <Typography component="span" sx={{ color: alpha(NAVY, 0.45), fontWeight: 400, fontSize: '0.75rem', ml: 0.5 }}>
          (opcional)
        </Typography>
      )}
    </Typography>
    {children}
    {(error || hint) && (
      <Typography
        sx={{
          mt: 0.625,
          fontFamily: 'Jost, sans-serif',
          fontSize: '0.75rem',
          color: error ? '#E7233B' : alpha(NAVY, 0.55),
          lineHeight: 1.45,
        }}
      >
        {error || hint}
      </Typography>
    )}
  </Box>
);

interface SearchableMultiSelectOption {
  value: string;
  label: string;
  /** Optional sub-label rendered in muted style on the right (e.g., empresa). */
  caption?: string;
}

interface SearchableMultiSelectProps {
  options: SearchableMultiSelectOption[];
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  error?: boolean;
}

/**
 * Searchable multi-select with checkbox rows. See design-system §21.9 (Field Type Decision Matrix):
 * the right pattern when picking multiple from a long list — checkbox per row, search by typing.
 */
const SearchableMultiSelect: FC<SearchableMultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Selecciona una o más opciones',
  searchPlaceholder = 'Buscar…',
  emptyMessage = 'Sin resultados.',
  disabled,
  error,
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [query, setQuery] = useState('');

  const selectedOptions = useMemo(() => options.filter((opt) => value.includes(opt.value)), [options, value]);

  const filteredOptions = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(q) || opt.caption?.toLowerCase().includes(q));
  }, [options, query]);

  const toggle = (optValue: string) => {
    if (value.includes(optValue)) {
      onChange(value.filter((v) => v !== optValue));
    } else {
      onChange([...value, optValue]);
    }
  };

  const baseRing = error
    ? `inset 0 0 0 1.5px #E7233B, 0 0 0 4px ${alpha('#E7233B', 0.18)}`
    : `inset 0 0 0 1px ${alpha(NAVY, 0.08)}`;
  const focusRing = `inset 0 0 0 1.5px ${NAVY}, 0 0 0 4px ${alpha(YELLOW, 0.25)}`;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ width: '100%', position: 'relative' }}>
        <Box
          ref={setAnchorEl}
          onClick={() => !disabled && setOpen((v) => !v)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 0.5,
            minHeight: 44,
            py: 1,
            px: 1.5,
            borderRadius: RADIUS_ITEM,
            bgcolor: SURFACE_CONTAINER_LOWEST,
            boxShadow: open ? focusRing : baseRing,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.55 : 1,
            transition: 'box-shadow 0.18s ease',
            '&:hover': !disabled && !open ? { boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.18)}` } : undefined,
          }}
        >
          {selectedOptions.length === 0 ? (
            <Typography sx={{ color: alpha(NAVY, 0.35), fontFamily: 'Jost, sans-serif', fontSize: '0.9375rem' }}>
              {placeholder}
            </Typography>
          ) : (
            selectedOptions.map((opt) => (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                sx={chipSx}
                onDelete={(e) => {
                  e.stopPropagation();
                  toggle(opt.value);
                }}
                onMouseDown={(e) => e.stopPropagation()}
              />
            ))
          )}
          <Box sx={{ ml: 'auto', color: alpha(NAVY, 0.45), display: 'flex', alignItems: 'center' }}>
            <SearchIcon fontSize="small" />
          </Box>
        </Box>

        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
          style={{ zIndex: 1400, width: anchorEl?.clientWidth }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: RADIUS_CARD,
              backdropFilter: GLASS_BLUR,
              backgroundColor: GLASS_BACKGROUND,
              boxShadow: SHADOW,
              border: `1px solid ${alpha(NAVY, 0.06)}`,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderBottom: `1px solid ${alpha(NAVY, 0.06)}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <InputBase
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                fullWidth
                startAdornment={
                  <InputAdornment position="start" sx={{ color: alpha(NAVY, 0.45) }}>
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                }
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontSize: '0.9375rem',
                  color: NAVY,
                  '& input::placeholder': { color: alpha(NAVY, 0.4), opacity: 1 },
                }}
              />
            </Box>
            <Box sx={{ maxHeight: 280, overflowY: 'auto' }}>
              {filteredOptions.length === 0 ? (
                <Typography
                  sx={{
                    px: 2,
                    py: 1.5,
                    color: alpha(NAVY, 0.55),
                    fontFamily: 'Jost, sans-serif',
                    fontSize: '0.875rem',
                  }}
                >
                  {emptyMessage}
                </Typography>
              ) : (
                <MenuList sx={{ py: 0.5 }} disablePadding>
                  {filteredOptions.map((opt) => {
                    const checked = value.includes(opt.value);
                    return (
                      <ListItemButton
                        key={opt.value}
                        onClick={() => toggle(opt.value)}
                        sx={{
                          mx: 0.75,
                          borderRadius: RADIUS_ITEM,
                          fontFamily: 'Jost, sans-serif',
                          '&:hover': { bgcolor: alpha(NAVY, 0.04) },
                        }}
                      >
                        <Checkbox
                          checked={checked}
                          tabIndex={-1}
                          disableRipple
                          icon={
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: '4px',
                                boxShadow: `inset 0 0 0 1.5px ${alpha(NAVY, 0.25)}`,
                              }}
                            />
                          }
                          checkedIcon={
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: '4px',
                                bgcolor: NAVY,
                                color: YELLOW,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <CheckIcon sx={{ fontSize: 14 }} />
                            </Box>
                          }
                          sx={{ p: 0, mr: 1.25 }}
                        />
                        <ListItemText
                          primary={opt.label}
                          secondary={opt.caption}
                          slotProps={{
                            primary: {
                              sx: {
                                fontFamily: 'Jost, sans-serif',
                                fontSize: '0.9375rem',
                                color: NAVY,
                                fontWeight: 500,
                              },
                            },
                            secondary: {
                              sx: { fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', color: alpha(NAVY, 0.55) },
                            },
                          }}
                        />
                      </ListItemButton>
                    );
                  })}
                </MenuList>
              )}
            </Box>
            {selectedOptions.length > 0 && (
              <Box
                sx={{
                  borderTop: `1px solid ${alpha(NAVY, 0.06)}`,
                  px: 1.5,
                  py: 1,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.75rem', color: alpha(NAVY, 0.55) }}>
                  {selectedOptions.length} seleccionada{selectedOptions.length === 1 ? '' : 's'}
                </Typography>
                <Box
                  component="button"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange([]);
                  }}
                  sx={{
                    border: 'none',
                    background: 'transparent',
                    color: NAVY,
                    fontFamily: 'Jost, sans-serif',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    p: 0.5,
                    borderRadius: RADIUS_ITEM,
                    '&:hover': { bgcolor: alpha(NAVY, 0.06) },
                  }}
                >
                  Limpiar
                </Box>
              </Box>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

const multiSelectSx = {
  width: '100%',
  '&.MuiInputBase-root, &.MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiOutlinedInput-root': {
    borderRadius: RADIUS_ITEM,
    bgcolor: '#ffffff',
    boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.08)}`,
    transition: 'box-shadow 0.18s ease',
    fontFamily: 'Jost, sans-serif',
    fontSize: '0.9375rem',
    '& fieldset': { border: 'none' },
    '&:hover': { boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.18)}` },
    '&.Mui-focused': {
      boxShadow: `inset 0 0 0 1.5px ${NAVY}, 0 0 0 4px ${alpha(YELLOW, 0.25)}`,
    },
    '&.Mui-disabled': {
      boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.06)}`,
      bgcolor: '#ffffff',
    },
  },
  '& .MuiSelect-select': { py: 1.25, px: 1.75, color: NAVY, minHeight: '24px' },
} as const;

// branchOffice is stored as a single comma-separated string.
const splitBranches = (s?: string | null) =>
  (s || '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);

interface ToggleRowProps {
  label: string;
  helper?: ReactNode;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}

const ToggleRow: FC<ToggleRowProps> = ({ label, helper, checked, disabled, onChange }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 2,
      py: 1.25,
      px: 1.25,
      borderRadius: RADIUS_ITEM,
      transition: 'background-color 0.18s ease',
      '&:hover': { bgcolor: alpha(NAVY, 0.03) },
      opacity: disabled ? 0.55 : 1,
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        component="label"
        sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.875rem', fontWeight: 500, color: NAVY, display: 'block' }}
      >
        {label}
      </Typography>
      {helper && (
        <Typography
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontSize: '0.75rem',
            color: alpha(NAVY, 0.55),
            mt: 0.25,
            lineHeight: 1.45,
          }}
        >
          {helper}
        </Typography>
      )}
    </Box>
    <FormControlLabel
      control={
        <Switch
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': { color: NAVY },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: YELLOW, opacity: 1 },
          }}
        />
      }
      label=""
      sx={{ m: 0 }}
    />
  </Box>
);

// ── Role options (unchanged business rule) ─────────────────────────────────

const getRoleOptions = (currentUser: User) => {
  const ROLE_OPTIONS = [
    { value: 'seller', label: 'Vendedor' },
    { value: 'sellerWithAssignment', label: 'Vendedor con Asignación' },
    { value: 'branchManager', label: 'Jefe de Sucursal' },
    { value: 'businessUnitAdmin', label: 'Administrador de Unidad de Negocio' },
  ];
  if (currentUser.isAdmin || currentUser.role === 'admin' || currentUser.role === 'businessUnitAdmin') {
    return ROLE_OPTIONS;
  }
  if (currentUser.role === 'branchManager') {
    return ROLE_OPTIONS.filter((role) => role.value !== 'businessUnitAdmin');
  }
  return [];
};

// Field-key → human label, used in the top-level error summary (§21.5).
const FIELD_LABELS: Partial<Record<keyof UserAttributes, string>> = {
  email: 'Email',
  name: 'Nombre',
  rut: 'RUT',
  businessUnitIds: 'Unidades de Negocio',
  assignmentTeam: 'Equipo de asignación',
};

const BCI_REFI_ROBOT_BUSINESS_UNIT_ID = 78;

// Roles that receive auto-assigned clients — the only ones a BCI team applies to.
const isSellerRole = (role: UserAttributes['role']): boolean => role === 'seller' || role === 'sellerWithAssignment';

interface Props {
  open: boolean;
  onClose: () => void;
  user?: User | null;
  onSuccess: () => void;
}

const UserFormModal: FC<Props> = ({ open, onClose, user = null, onSuccess }) => {
  const currentUser = useCurrentUser();
  const isEditMode = !!user;

  const [formData, setFormData] = useState<UserAttributes>({
    email: '',
    businessUnitIds: [],
    name: '',
    phone: '',
    rut: '',
    brandsToSell: [],
    branchOffice: '',
    sources: [],
    role: 'seller',
    isAssignable: true,
    allowedTemplateNames: [],
    allowAllTemplates: false,
    allowTemplateCreation: false,
    reportEmailFrequency: null,
    assignedSellerIds: [],
    workingHours: null,
    isAway: false,
  });

  const [sendEmail, setSendEmail] = useState(true);
  const [errors, setErrors] = useState<Partial<Record<keyof UserAttributes, string>>>({});
  const [emailTouched, setEmailTouched] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [submitErrorMsg, setSubmitErrorMsg] = useState<string | null>(null);
  const [newSource, setNewSource] = useState('');
  const [newBranch, setNewBranch] = useState('');
  const [availableTemplates, setAvailableTemplates] = useState<string[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [availableBrands, setAvailableBrands] = useState<string[]>([]);
  const [availableSources, setAvailableSources] = useState<string[]>([]);
  const [availableBranchOffices, setAvailableBranchOffices] = useState<string[]>([]);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [warningAcknowledged, setWarningAcknowledged] = useState(false);
  const [originalBranchOffice, setOriginalBranchOffice] = useState<string>('');
  const [originalBrandsToSell, setOriginalBrandsToSell] = useState<string[]>([]);
  const [originalIsAssignable, setOriginalIsAssignable] = useState<boolean>(true);

  const dispatch = useDispatch<AppDispatch>();
  const { data: businessUnits } = useGetUserBusinessUnitsQuery(null);
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();

  const canAssignSellers = !!currentUser && hasPermission(currentUser, 'users', 'assignSellers');
  const showAssignedSellers = canAssignSellers && formData.role === 'branchManager';
  const assignedSellersBusinessUnitId = formData.businessUnitIds[0];
  const { data: assignableUsersList } = useGetUsersQuery(
    assignedSellersBusinessUnitId != null ? { businessUnitId: assignedSellersBusinessUnitId } : {},
    { skip: !showAssignedSellers || formData.businessUnitIds.length === 0 }
  );
  const availableSellers = useMemo(() => {
    if (!assignableUsersList) return [];
    return assignableUsersList.filter(
      (u) =>
        (u.role === 'seller' || u.role === 'sellerWithAssignment') &&
        u.id !== user?.id &&
        u.businessUnitIds?.some((id) => formData.businessUnitIds.includes(id))
    );
  }, [assignableUsersList, formData.businessUnitIds, user?.id]);

  // Initialize / reset form data when user changes
  useEffect(() => {
    if (user) {
      const initialBranchOffice = user.branchOffice || '';
      const initialBrandsToSell = user.brandsToSell || [];
      const initialIsAssignable = user.isAssignable ?? true;
      setFormData({
        email: user.email,
        businessUnitIds: user.businessUnitIds || [],
        name: user.name || '',
        phone: user.phone || '',
        rut: user.rut || '',
        brandsToSell: initialBrandsToSell,
        branchOffice: initialBranchOffice,
        sources: user.sources || [],
        role: user.role,
        isAssignable: initialIsAssignable,
        allowedTemplateNames: user.allowedTemplateNames || [],
        allowAllTemplates: user.allowAllTemplates || false,
        allowTemplateCreation: user.allowTemplateCreation || false,
        reportEmailFrequency: user.reportEmailFrequency ?? null,
        assignedSellerIds: user.assignedSellerIds || [],
        workingHours: user.workingHours ?? null,
        isAway: user.isAway ?? false,
        assignmentTeam: user.assignmentTeam ?? null,
      });
      setOriginalBranchOffice(initialBranchOffice);
      setOriginalBrandsToSell([...initialBrandsToSell]);
      setOriginalIsAssignable(initialIsAssignable);
    } else {
      setFormData({
        email: '',
        businessUnitIds: [],
        name: '',
        phone: '',
        rut: '',
        brandsToSell: [],
        branchOffice: '',
        sources: [],
        role: 'seller',
        isAssignable: true,
        allowedTemplateNames: [],
        allowAllTemplates: false,
        allowTemplateCreation: false,
        reportEmailFrequency: null,
        assignedSellerIds: [],
        workingHours: null,
        isAway: false,
        assignmentTeam: null,
      });
      setOriginalBranchOffice('');
      setOriginalBrandsToSell([]);
      setOriginalIsAssignable(true);
    }
    setErrors({});
    setEmailTouched(false);
    setSubmitFailed(false);
    setSubmitErrorMsg(null);
    setWarningAcknowledged(false);
    setWarningDialogOpen(false);
  }, [user]);

  // Fetch templates for all selected business units
  useEffect(() => {
    if (formData.allowAllTemplates || !formData.businessUnitIds || formData.businessUnitIds.length === 0) {
      setAvailableTemplates([]);
      setLoadingTemplates(false);
      return;
    }

    const fetchTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const templatePromises: Promise<TemplateResponse>[] = formData.businessUnitIds.map(
          async (businessUnitId): Promise<TemplateResponse> => {
            try {
              const resultPromise = dispatch(
                businessUnitsApi.endpoints.getBusinessUnitTemplates.initiate(businessUnitId)
              );
              const result = await (resultPromise as unknown as Promise<{ data?: TemplateResponse }>);
              if ('data' in result && result.data) return result.data;
              return [] as unknown as TemplateResponse;
            } catch (error) {
              console.warn(`[UserFormModal] Error fetching templates for BU ${businessUnitId}:`, error);
              return [] as unknown as TemplateResponse;
            }
          }
        );
        const templatesResults = await Promise.all(templatePromises);
        const allTemplates: string[] = [];
        templatesResults.forEach((templates) => {
          templates.forEach((template) => {
            if (!allTemplates.includes(template.name)) allTemplates.push(template.name);
          });
        });
        setAvailableTemplates(allTemplates.sort());
      } catch (error) {
        console.error('[UserFormModal] Error fetching templates:', error);
        setAvailableTemplates([]);
      } finally {
        setLoadingTemplates(false);
      }
    };

    fetchTemplates().catch(() => {});
  }, [formData.businessUnitIds, formData.allowAllTemplates, dispatch]);

  // Fetch allowed brands and sources for selected business units
  useEffect(() => {
    if (!formData.businessUnitIds || formData.businessUnitIds.length === 0) {
      setAvailableBrands([]);
      setAvailableSources([]);
      return;
    }
    const fetchBrandsAndSources = async () => {
      try {
        const brandPromises = formData.businessUnitIds.map(async (buId) => {
          try {
            const result = await (dispatch(
              businessUnitsApi.endpoints.getBusinessUnitAllowedBrands.initiate({ businessUnitId: buId })
            ) as unknown as Promise<{ data?: string[] }>);
            return result.data || [];
          } catch {
            return [];
          }
        });
        const sourcePromises = formData.businessUnitIds.map(async (buId) => {
          try {
            const result = await (dispatch(
              businessUnitsApi.endpoints.getBusinessUnitSources.initiate({ businessUnitId: buId })
            ) as unknown as Promise<{ data?: string[] }>);
            return result.data || [];
          } catch {
            return [];
          }
        });
        const [brandsResults, sourcesResults] = await Promise.all([
          Promise.all(brandPromises),
          Promise.all(sourcePromises),
        ]);
        setAvailableBrands([...new Set(brandsResults.flat())].filter((b) => b && b.trim() !== '').sort());
        setAvailableSources([...new Set(sourcesResults.flat())].filter((s) => s && s.trim() !== '').sort());
      } catch {
        setAvailableBrands([]);
        setAvailableSources([]);
      }
    };
    fetchBrandsAndSources().catch(() => {});
  }, [formData.businessUnitIds, dispatch]);

  // Fetch branch offices scoped to selected brands
  useEffect(() => {
    if (!formData.businessUnitIds || formData.businessUnitIds.length === 0) {
      setAvailableBranchOffices([]);
      return;
    }
    const brandsArg = formData.brandsToSell && formData.brandsToSell.length > 0 ? formData.brandsToSell : undefined;
    const fetchBranches = async () => {
      try {
        const promises = formData.businessUnitIds.map(async (buId) => {
          try {
            const result = await (dispatch(
              businessUnitsApi.endpoints.getBusinessUnitBranchOffices.initiate({
                businessUnitId: buId,
                brands: brandsArg,
              })
            ) as unknown as Promise<{ data?: string[] }>);
            return result.data || [];
          } catch {
            return [];
          }
        });
        const results = await Promise.all(promises);
        setAvailableBranchOffices([...new Set(results.flat())].filter((b) => b && b.trim() !== '').sort());
      } catch {
        setAvailableBranchOffices([]);
      }
    };
    fetchBranches().catch(() => {});
  }, [formData.businessUnitIds, formData.brandsToSell, dispatch]);

  const handleInputChange = (field: keyof UserAttributes, value: string | string[] | number[] | boolean | null) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    if (submitFailed) setSubmitFailed(false);

    if (isEditMode && (field === 'branchOffice' || field === 'brandsToSell' || field === 'isAssignable')) {
      const branchOfficeChanged = field === 'branchOffice' && value !== originalBranchOffice;
      const brandsChanged =
        field === 'brandsToSell' &&
        JSON.stringify([...(value as string[])].sort()) !== JSON.stringify([...originalBrandsToSell].sort());
      const isAssignableChanged = field === 'isAssignable' && value !== originalIsAssignable;

      if ((branchOfficeChanged || brandsChanged || isAssignableChanged) && !warningAcknowledged) {
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        setWarningDialogOpen(true);
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof UserAttributes, string>> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'El email es obligatorio';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Formato esperado: nombre@empresa.com';
    }
    if (!formData.name?.trim()) newErrors.name = 'El nombre es obligatorio';
    if (formData.rut && !validateRut(formData.rut)) newErrors.rut = 'El RUT no es válido';
    if (!formData.businessUnitIds.length) {
      newErrors.businessUnitIds = 'Selecciona al menos una unidad de negocio';
    } else {
      const companyIds = businessUnits
        ?.filter((bu) => formData.businessUnitIds?.includes(bu.id))
        .map((bu) => bu.company?.id);
      if (new Set(companyIds).size !== 1) {
        newErrors.businessUnitIds = 'Las unidades deben pertenecer a la misma empresa';
      }
    }
    // BCI refi sellers are routed by team, so a team is mandatory for BU 78 sellers.
    if (
      formData.businessUnitIds.includes(BCI_REFI_ROBOT_BUSINESS_UNIT_ID) &&
      isSellerRole(formData.role) &&
      !formData.assignmentTeam
    ) {
      newErrors.assignmentTeam = 'Selecciona el equipo de asignación';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    setSubmitErrorMsg(null);
    if (!validateForm()) {
      setSubmitFailed(true);
      // Scroll the dialog to the top so the error summary is visible
      const titleEl = document.getElementById('user-form-dialog-title');
      titleEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    setSubmitFailed(false);

    if (isEditMode) {
      const branchOfficeChanged = formData.branchOffice !== originalBranchOffice;
      const brandsChanged =
        JSON.stringify([...(formData.brandsToSell || [])].sort()) !== JSON.stringify([...originalBrandsToSell].sort());
      const isAssignableChanged = formData.isAssignable !== originalIsAssignable;
      if ((branchOfficeChanged || brandsChanged || isAssignableChanged) && !warningAcknowledged) {
        if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
        setWarningDialogOpen(true);
        return;
      }
    }

    if (isEditMode && user) {
      updateUser({ userId: user.id, userAttributes: formData })
        .unwrap()
        .then(() => {
          onSuccess();
          onClose();
        })
        .catch((error) => {
          console.error('[UserFormModal] Error updating user:', error);
          setSubmitErrorMsg('No pudimos guardar los cambios. Inténtalo nuevamente en unos segundos.');
        });
    } else {
      createUser({ userAttributes: formData, sendEmail })
        .unwrap()
        .then(() => {
          onSuccess();
          onClose();
        })
        .catch((error) => {
          console.error('[UserFormModal] Error creating user:', error);
          setSubmitErrorMsg('No pudimos crear el usuario. Inténtalo nuevamente en unos segundos.');
        });
    }
  };

  const handleAddSource = () => {
    if (newSource.trim() && !formData.sources?.includes(newSource.trim())) {
      const updatedSources = [...(formData.sources || []), newSource.trim()];
      handleInputChange('sources', updatedSources);
      setNewSource('');
    }
  };

  const handleSourceKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddSource();
    }
  };

  const handleAddBranch = () => {
    const t = newBranch.trim();
    const branches = splitBranches(formData.branchOffice);
    if (t && !branches.includes(t)) {
      handleInputChange('branchOffice', [...branches, t].join(', '));
      setNewBranch('');
    }
  };

  const handleBranchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddBranch();
    }
  };

  const handleWarningAcknowledge = () => {
    setWarningAcknowledged(true);
    setWarningDialogOpen(false);
  };

  const handleWarningCancel = () => {
    setWarningDialogOpen(false);
    if (isEditMode && user) {
      setFormData((prev) => ({
        ...prev,
        branchOffice: originalBranchOffice,
        brandsToSell: [...originalBrandsToSell],
        isAssignable: originalIsAssignable,
      }));
    }
  };

  const handleModalClose = () => {
    setWarningAcknowledged(false);
    setWarningDialogOpen(false);
    onClose();
  };

  const handleRemoveTemplate = (templateToRemove: string) => {
    const updatedTemplates = (formData.allowedTemplateNames || []).filter((t) => t !== templateToRemove);
    handleInputChange('allowedTemplateNames', updatedTemplates);
  };

  const canCreate = !isEditMode && currentUser && hasPermission(currentUser, 'users', 'create', formData);
  const canUpdate = isEditMode && user && currentUser && hasPermission(currentUser, 'users', 'update', user);
  const canSubmit = isEditMode ? canUpdate : canCreate;

  const selectedBUs = (businessUnits ?? []).filter((bu) => formData.businessUnitIds.includes(bu.id));
  const hasCarDealershipBU = selectedBUs.some((bu) => bu.type === 'car_dealership');
  // BCI refi (BU 78) routes clients to two seller teams by salary, so its sellers pick a team.
  const hasBciRefiBU = formData.businessUnitIds.includes(BCI_REFI_ROBOT_BUSINESS_UNIT_ID);
  const hasBUSelected = formData.businessUnitIds.length > 0;

  const emailIsValid = useMemo(() => !!formData.email && validateEmail(formData.email), [formData.email]);
  const rutIsValid = useMemo(() => !!formData.rut && validateRut(formData.rut), [formData.rut]);

  const errorSummary = useMemo(() => {
    const list = (Object.keys(errors) as (keyof UserAttributes)[])
      .filter((key) => errors[key])
      .map((key) => FIELD_LABELS[key] ?? key);
    return list;
  }, [errors]);

  if (!currentUser) return null;

  const isInFlight = isCreating || isUpdating;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleModalClose}
        maxWidth="md"
        fullWidth
        slotProps={{ paper: { sx: glassPaperSx }, backdrop: { sx: glassBackdropSx } }}
      >
        <DialogTitle
          id="user-form-dialog-title"
          sx={{ color: NAVY, fontWeight: 700, fontFamily: 'Jost, sans-serif', fontSize: '1.25rem' }}
        >
          {isEditMode ? 'Editar usuario' : 'Crear usuario'}
        </DialogTitle>
        <DialogContent sx={{ fontFamily: 'Jost, sans-serif' }}>
          {submitFailed && errorSummary.length > 0 && (
            <InlineFeedback severity="error" sx={{ mb: 2, mt: 1 }}>
              {errorSummary.length === 1
                ? `Falta 1 dato: ${errorSummary[0]}.`
                : `Faltan ${errorSummary.length} datos: ${errorSummary.join(', ')}.`}{' '}
              Revisa los campos marcados.
            </InlineFeedback>
          )}
          {submitErrorMsg && (
            <InlineFeedback severity="error" sx={{ mb: 2, mt: 1 }}>
              {submitErrorMsg}
            </InlineFeedback>
          )}

          <FormSection eyebrow="Identidad" title="¿Quién es este usuario?" dense>
            <FormGrid columns={2} gap={2}>
              <FormField
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                onBlur={() => setEmailTouched(true)}
                error={errors.email}
                success={emailIsValid && !errors.email && emailTouched}
                placeholder="usuario@empresa.com"
                required
              />
              <FormField
                label="Nombre"
                value={formData.name ?? ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={errors.name}
                placeholder="Ej: María Pérez"
                required
              />
              <FormField
                label="Teléfono"
                value={formData.phone ?? ''}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="+56 9 1234 5678"
                optional
              />
              <RutField
                label="RUT"
                value={formData.rut ?? ''}
                onChange={(rut) => handleInputChange('rut', rut)}
                error={errors.rut}
                success={rutIsValid && !errors.rut}
                optional
              />
            </FormGrid>
          </FormSection>

          <FormSection eyebrow="Organización" title="¿Dónde trabaja?" dense>
            <FormGrid columns={1} gap={2}>
              <FieldShell
                label="Unidades de negocio"
                required
                error={errors.businessUnitIds}
                hint={
                  !errors.businessUnitIds ? 'Busca y marca una o más. Deben pertenecer a la misma empresa.' : undefined
                }
              >
                <SearchableMultiSelect
                  options={(businessUnits ?? []).map((bu) => ({
                    value: bu.id.toString(),
                    label: bu.name,
                    caption: bu.company?.name,
                  }))}
                  value={formData.businessUnitIds.map(String)}
                  onChange={(next) => handleInputChange('businessUnitIds', next.map(Number))}
                  placeholder="Selecciona una o más unidades"
                  searchPlaceholder="Buscar por nombre o empresa…"
                  emptyMessage="No encontramos unidades con ese nombre."
                  error={!!errors.businessUnitIds}
                />
              </FieldShell>

              <FormGrid columns={2} gap={2}>
                <FormField
                  select
                  label="Rol"
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                >
                  {getRoleOptions(currentUser).map((option) => (
                    <MenuItem key={option.value} value={option.value} sx={{ fontFamily: 'Jost, sans-serif' }}>
                      {option.label}
                    </MenuItem>
                  ))}
                </FormField>

                <FieldShell
                  label="Sucursales"
                  optional
                  hint={
                    hasBUSelected && availableBranchOffices.length === 0
                      ? 'Esta unidad de negocio no tiene sucursales configuradas. Escribe el nombre y presiona Enter para agregarla.'
                      : 'Selecciona una o más, o escribe el nombre de una nueva y presiona Enter.'
                  }
                >
                  <Stack spacing={1.25}>
                    <FormControl fullWidth disabled={!hasBUSelected}>
                      <Select
                        multiple
                        displayEmpty
                        value={splitBranches(formData.branchOffice)}
                        onChange={(e) =>
                          handleInputChange('branchOffice', (e.target.value as string[]).join(', ') || null)
                        }
                        sx={multiSelectSx}
                        renderValue={(selected) => {
                          if ((selected as string[]).length === 0) {
                            return (
                              <Typography
                                sx={{
                                  color: alpha(NAVY, 0.35),
                                  fontFamily: 'Jost, sans-serif',
                                  fontSize: '0.9375rem',
                                }}
                              >
                                Selecciona una o más sucursales
                              </Typography>
                            );
                          }
                          return (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {(selected as string[]).map((value) => (
                                <Chip
                                  key={value}
                                  label={value}
                                  size="small"
                                  sx={chipSx}
                                  onDelete={() =>
                                    handleInputChange(
                                      'branchOffice',
                                      splitBranches(formData.branchOffice)
                                        .filter((b) => b !== value)
                                        .join(', ') || null
                                    )
                                  }
                                  onMouseDown={(e) => e.stopPropagation()}
                                />
                              ))}
                            </Box>
                          );
                        }}
                      >
                        {availableBranchOffices.map((name) => (
                          <MenuItem key={name} value={name} sx={{ fontFamily: 'Jost, sans-serif' }}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <FormField
                          value={newBranch}
                          onChange={(e) => setNewBranch(e.target.value)}
                          onKeyDown={handleBranchKeyDown}
                          placeholder="Agregar una sucursal nueva y presionar Enter"
                        />
                      </Box>
                      <IconButton
                        onClick={handleAddBranch}
                        disabled={!newBranch.trim() || splitBranches(formData.branchOffice).includes(newBranch.trim())}
                        size="medium"
                        aria-label="Agregar sucursal"
                        sx={{
                          color: NAVY,
                          backgroundColor: alpha(NAVY, 0.06),
                          borderRadius: RADIUS_ITEM,
                          height: 40,
                          width: 40,
                          '&:hover': { backgroundColor: alpha(NAVY, 0.12) },
                          '&.Mui-disabled': { color: alpha(NAVY, 0.3) },
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </Box>
                  </Stack>
                </FieldShell>

                {hasBciRefiBU && isSellerRole(formData.role) && (
                  <FormField
                    select
                    required
                    label="Equipo de asignación"
                    value={formData.assignmentTeam || ''}
                    error={errors.assignmentTeam}
                    onChange={(e) =>
                      handleInputChange('assignmentTeam', (e.target.value || null) as AssignmentTeam | null)
                    }
                    hint="Fuerza de venta recibe clientes con renta ≥ $1.600.000; centro de servicio, el resto."
                  >
                    <MenuItem value="" disabled sx={{ fontFamily: 'Jost, sans-serif' }}>
                      <em>Selecciona un equipo</em>
                    </MenuItem>
                    <MenuItem value="service_center" sx={{ fontFamily: 'Jost, sans-serif' }}>
                      Centro de servicio
                    </MenuItem>
                    <MenuItem value="sales_force" sx={{ fontFamily: 'Jost, sans-serif' }}>
                      Fuerza de venta
                    </MenuItem>
                  </FormField>
                )}
              </FormGrid>

              {currentUser.isAdmin && (
                <FormField
                  select
                  label="Frecuencia de correos de reportes"
                  value={formData.reportEmailFrequency || ''}
                  onChange={(e) =>
                    handleInputChange(
                      'reportEmailFrequency',
                      (e.target.value || null) as 'daily' | 'weekly' | 'monthly' | null
                    )
                  }
                  hint="Recibirá un resumen automático según la frecuencia que elijas."
                  optional
                >
                  <MenuItem value="" sx={{ fontFamily: 'Jost, sans-serif' }}>
                    <em>Sin correos</em>
                  </MenuItem>
                  <MenuItem value="daily" sx={{ fontFamily: 'Jost, sans-serif' }}>
                    Diario
                  </MenuItem>
                  <MenuItem value="weekly" sx={{ fontFamily: 'Jost, sans-serif' }}>
                    Semanal
                  </MenuItem>
                  <MenuItem value="monthly" sx={{ fontFamily: 'Jost, sans-serif' }}>
                    Mensual
                  </MenuItem>
                </FormField>
              )}
            </FormGrid>
          </FormSection>

          <FormSection eyebrow="Vendedor" title="¿Cómo trabajará?" dense>
            <ToggleRow
              label="Asignable a clientes"
              helper="Los nuevos clientes podrán asignarse automáticamente a este usuario."
              checked={formData.isAssignable ?? true}
              onChange={(v) => handleInputChange('isAssignable', v)}
            />

            {(formData.isAssignable ?? true) && (
              <ToggleRow
                label="Fuera de oficina"
                helper="Si está activado, el usuario no recibirá asignaciones automáticas aunque esté dentro de su horario."
                checked={formData.isAway ?? false}
                onChange={(v) => setFormData((prev) => ({ ...prev, isAway: v }))}
              />
            )}

            {(formData.isAssignable ?? true) && (
              <Box sx={{ py: 1.25, px: 1.25, borderRadius: RADIUS_ITEM }}>
                <Box sx={{ mb: 1.25 }}>
                  <Typography
                    component="label"
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: NAVY,
                      display: 'block',
                    }}
                  >
                    Horario laboral
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Jost, sans-serif',
                      fontSize: '0.75rem',
                      color: alpha(NAVY, 0.55),
                      mt: 0.25,
                      lineHeight: 1.45,
                    }}
                  >
                    Activa los días y elige el rango de horas en que este usuario está disponible para recibir clientes.
                    Fuera de este horario no será asignado automáticamente.
                  </Typography>
                </Box>
                <WorkingHoursEditor
                  value={formData.workingHours ?? null}
                  onChange={(next) => setFormData((prev) => ({ ...prev, workingHours: next }))}
                />
              </Box>
            )}

            {showAssignedSellers && (
              <Box sx={{ mt: 2 }}>
                <FieldShell
                  label="Vendedores asignados"
                  optional
                  hint={
                    availableSellers.length === 0
                      ? 'No hay vendedores en esta unidad para asignar.'
                      : 'Si dejas esto vacío, este gerente verá todos los vendedores que coincidan con sus marcas y sucursal.'
                  }
                >
                  <FormControl fullWidth disabled={availableSellers.length === 0}>
                    <Select
                      multiple
                      displayEmpty
                      value={(formData.assignedSellerIds || []).map(String)}
                      onChange={(e) => handleInputChange('assignedSellerIds', (e.target.value as string[]).map(Number))}
                      sx={multiSelectSx}
                      renderValue={(selected) => {
                        const selectedIds = selected as string[];
                        if (selectedIds.length === 0) {
                          return (
                            <Typography
                              sx={{
                                color: alpha(NAVY, 0.35),
                                fontFamily: 'Jost, sans-serif',
                                fontSize: '0.9375rem',
                              }}
                            >
                              Selecciona vendedores
                            </Typography>
                          );
                        }
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selectedIds.map((value) => {
                              const seller = availableSellers.find((s) => String(s.id) === value);
                              const label = seller?.name || seller?.email || `#${value}`;
                              return (
                                <Chip
                                  key={value}
                                  label={label}
                                  size="small"
                                  sx={chipSx}
                                  onDelete={() =>
                                    handleInputChange(
                                      'assignedSellerIds',
                                      (formData.assignedSellerIds || []).filter((id) => String(id) !== value)
                                    )
                                  }
                                  onMouseDown={(e) => e.stopPropagation()}
                                />
                              );
                            })}
                          </Box>
                        );
                      }}
                    >
                      {availableSellers.map((seller) => (
                        <MenuItem key={seller.id} value={String(seller.id)} sx={{ fontFamily: 'Jost, sans-serif' }}>
                          {seller.name || seller.email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FieldShell>
              </Box>
            )}

            {hasCarDealershipBU && (
              <Box sx={{ mt: 2 }}>
                <FieldShell
                  label="Marcas que vende"
                  optional
                  hint={
                    availableBrands.length === 0
                      ? 'Esta unidad de negocio no tiene marcas configuradas.'
                      : 'Si dejas esto vacío, podrá vender todas las marcas de la unidad.'
                  }
                >
                  <FormControl fullWidth disabled={availableBrands.length === 0}>
                    <Select
                      multiple
                      displayEmpty
                      value={formData.brandsToSell || []}
                      onChange={(e) => handleInputChange('brandsToSell', e.target.value as string[])}
                      sx={multiSelectSx}
                      renderValue={(selected) => {
                        if ((selected as string[]).length === 0) {
                          return (
                            <Typography
                              sx={{
                                color: alpha(NAVY, 0.35),
                                fontFamily: 'Jost, sans-serif',
                                fontSize: '0.9375rem',
                              }}
                            >
                              Selecciona marcas
                            </Typography>
                          );
                        }
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as string[]).map((value) => (
                              <Chip
                                key={value}
                                label={value}
                                size="small"
                                sx={chipSx}
                                onDelete={() =>
                                  handleInputChange(
                                    'brandsToSell',
                                    (formData.brandsToSell || []).filter((b) => b !== value)
                                  )
                                }
                                onMouseDown={(e) => e.stopPropagation()}
                              />
                            ))}
                          </Box>
                        );
                      }}
                    >
                      {availableBrands.map((brand) => (
                        <MenuItem key={brand} value={brand} sx={{ fontFamily: 'Jost, sans-serif' }}>
                          {brand}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FieldShell>
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <FieldShell label="Orígenes" optional hint="Etiquetas para clasificar los leads que atenderá.">
                <FormControl fullWidth>
                  <Select
                    multiple
                    displayEmpty
                    value={formData.sources || []}
                    onChange={(e) => handleInputChange('sources', e.target.value as string[])}
                    sx={multiSelectSx}
                    renderValue={(selected) => {
                      if ((selected as string[]).length === 0) {
                        return (
                          <Typography
                            sx={{ color: alpha(NAVY, 0.35), fontFamily: 'Jost, sans-serif', fontSize: '0.9375rem' }}
                          >
                            Selecciona o agrega orígenes
                          </Typography>
                        );
                      }
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected as string[]).map((value) => (
                            <Chip
                              key={value}
                              label={value}
                              size="small"
                              sx={chipSx}
                              onDelete={() =>
                                handleInputChange(
                                  'sources',
                                  (formData.sources || []).filter((s) => s !== value)
                                )
                              }
                              onMouseDown={(e) => e.stopPropagation()}
                            />
                          ))}
                        </Box>
                      );
                    }}
                  >
                    {availableSources.map((source) => (
                      <MenuItem key={source} value={source} sx={{ fontFamily: 'Jost, sans-serif' }}>
                        {source}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </FieldShell>
              <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <FormField
                    value={newSource}
                    onChange={(e) => setNewSource(e.target.value)}
                    onKeyDown={handleSourceKeyDown}
                    placeholder="Agregar un origen personalizado y presionar Enter"
                  />
                </Box>
                <IconButton
                  onClick={handleAddSource}
                  disabled={!newSource.trim() || formData.sources?.includes(newSource.trim())}
                  size="medium"
                  aria-label="Agregar origen"
                  sx={{
                    color: NAVY,
                    backgroundColor: alpha(NAVY, 0.06),
                    borderRadius: RADIUS_ITEM,
                    height: 40,
                    width: 40,
                    '&:hover': { backgroundColor: alpha(NAVY, 0.12) },
                    '&.Mui-disabled': { color: alpha(NAVY, 0.3) },
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>
          </FormSection>

          <FormSection eyebrow="Plantillas" title="Permisos sobre plantillas de WhatsApp" dense>
            <ToggleRow
              label="Acceso a todas las plantillas"
              helper="Si lo activas, podrá usar cualquier plantilla disponible en sus unidades."
              checked={formData.allowAllTemplates || false}
              onChange={(v) => {
                handleInputChange('allowAllTemplates', v);
                if (v) handleInputChange('allowedTemplateNames', []);
              }}
            />
            <ToggleRow
              label="Puede crear plantillas nuevas"
              helper="Permite proponer nuevas plantillas para aprobación de Meta."
              checked={formData.allowTemplateCreation || false}
              onChange={(v) => handleInputChange('allowTemplateCreation', v)}
            />

            {!formData.allowAllTemplates && (
              <Box sx={{ mt: 2 }}>
                <FieldShell
                  label="Plantillas permitidas"
                  optional
                  hint={
                    loadingTemplates
                      ? 'Cargando plantillas disponibles…'
                      : 'Selecciona específicamente cuáles plantillas podrá usar.'
                  }
                >
                  <FormControl fullWidth>
                    <Select
                      multiple
                      displayEmpty
                      value={formData.allowedTemplateNames || []}
                      onChange={(e) => handleInputChange('allowedTemplateNames', e.target.value as string[])}
                      disabled={loadingTemplates}
                      sx={multiSelectSx}
                      renderValue={(selected) => {
                        if ((selected as string[]).length === 0) {
                          return (
                            <Typography
                              sx={{
                                color: alpha(NAVY, 0.35),
                                fontFamily: 'Jost, sans-serif',
                                fontSize: '0.9375rem',
                              }}
                            >
                              Selecciona plantillas
                            </Typography>
                          );
                        }
                        return (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(selected as string[]).map((value) => (
                              <Chip key={value} label={value} size="small" sx={chipSx} />
                            ))}
                          </Box>
                        );
                      }}
                    >
                      {loadingTemplates ? (
                        <MenuItem disabled>
                          <Typography variant="body2" sx={{ color: alpha(NAVY, 0.5) }}>
                            Cargando plantillas…
                          </Typography>
                        </MenuItem>
                      ) : (
                        availableTemplates.map((template) => (
                          <MenuItem key={template} value={template} sx={{ fontFamily: 'Jost, sans-serif' }}>
                            {template}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </FieldShell>
                {formData.allowedTemplateNames && formData.allowedTemplateNames.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {formData.allowedTemplateNames.map((template) => (
                      <Chip
                        key={template}
                        label={template}
                        onDelete={() => handleRemoveTemplate(template)}
                        size="small"
                        sx={chipSx}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </FormSection>

          {!isEditMode && (
            <FormSection eyebrow="Bienvenida" title="Email de bienvenida" dense>
              {currentUser.isAdmin ? (
                <ToggleRow
                  label="Enviar email de bienvenida"
                  helper="Le enviaremos al usuario un correo con su contraseña inicial."
                  checked={sendEmail}
                  onChange={setSendEmail}
                />
              ) : (
                <Typography
                  sx={{
                    color: alpha(NAVY, 0.65),
                    fontFamily: 'Jost, sans-serif',
                    fontSize: '0.875rem',
                    px: 1.25,
                    py: 0.5,
                  }}
                >
                  Le enviaremos al usuario un email de bienvenida con la contraseña generada.
                </Typography>
              )}
            </FormSection>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
          <Stack direction="row" spacing={1.5} sx={{ width: '100%', justifyContent: 'flex-end' }}>
            <BackButton onClick={handleModalClose} disabled={isInFlight}>
              Cancelar
            </BackButton>
            <PrimaryButton onClick={handleSubmit} disabled={!canSubmit || isInFlight}>
              {isInFlight ? (isEditMode ? 'Guardando…' : 'Creando…') : isEditMode ? 'Guardar cambios' : 'Crear usuario'}
            </PrimaryButton>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Warning Dialog */}
      <Dialog
        open={warningDialogOpen}
        onClose={handleWarningCancel}
        disableEnforceFocus
        disableAutoFocus={false}
        aria-labelledby="warning-dialog-title"
        slotProps={{ paper: { sx: glassPaperSx }, backdrop: { sx: glassBackdropSx } }}
      >
        <DialogTitle
          id="warning-dialog-title"
          sx={{
            color: NAVY,
            fontWeight: 600,
            fontFamily: 'Jost, sans-serif',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <WarningAmberIcon sx={{ color: 'warning.main' }} />
          ¿Continuar con el cambio?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: alpha(NAVY, 0.75), fontFamily: 'Jost, sans-serif' }}>
            Esta modificación puede afectar la asignación automática de leads y otros flujos. Confirma para continuar.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <BackButton onClick={handleWarningCancel}>Cancelar</BackButton>
          <PrimaryButton onClick={handleWarningAcknowledge}>Continuar</PrimaryButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserFormModal;
